/**
 * 《录音授权协议》(红线 14.6).
 *
 * One agreement, three named uses. The student signs once; the API contract
 * is unchanged (train / display / commercial all go out as true), so the three
 * uses stay visible as numbered clauses and as the status list on 我的.
 */

/** Clause keys double as the three consent flags on the server. */
import {
  BENEFICIARY_NAME,
  GRANTEES_LABEL,
  ORG_NAME,
  ORG_SHORT,
  PARTNER_NAME,
} from "../../lib/constants";

export type ConsentUseKey = "train" | "display" | "commercial";

export interface ConsentClause {
  /** 一、二、三… */
  number: string;
  title: string;
  /** Set for the three clauses that map to a consent flag. */
  key?: ConsentUseKey;
  paragraphs: string[];
}

export const CONSENT_TITLE = "录音授权协议";

/** The plain-language summary above the fold. */
export const CONSENT_SUMMARY: string[] = [
  `本协议由你（录音者）与 ${ORG_NAME}（以下简称「${ORG_SHORT}」）签署；${ORG_SHORT}与${PARTNER_NAME}合作开展本项目。`,
  `签署后，你把本项目中你的录音及其派生数据的使用权授予 ${ORG_NAME} 及额外受益人 ${BENEFICIARY_NAME}老师（以下合称「${GRANTEES_LABEL}」）。`,
  "这些数据会用在三件事上：技术研究与模型训练、教学展示与成果汇报，以及将来可能收费的商业化产品。",
  "用到的地方都只出现你的编号（例如 XZ-S017），不出现姓名、学号和联系方式；原始录音不会公开发布。",
  `本协议不包含用你的声音做语音合成、克隆音色或广告代言，这些都需要${ORG_SHORT}另外单独征求你的同意。`,
  `你可以随时向${ORG_SHORT}提出书面申请撤回授权，不需要理由，也不影响课程成绩；撤回自我们收到申请之日起生效，只影响之后的采集与新的数据集版本。下面是完整条款，签署前可以先展开读一遍。`,
];

export const CONSENT_CLAUSES: ConsentClause[] = [
  {
    number: "一",
    title: "协议双方",
    paragraphs: [
      `本协议由你（以下称「录音者」）与 ${ORG_NAME}（以下简称「${ORG_SHORT}」）签署。`,
      `${ORG_SHORT}与${PARTNER_NAME}合作开展本项目：课程团队负责组织采集与教学使用，${ORG_SHORT}负责平台运行、数据保管与后续技术研发。`,
      "你在本平台上录制的音频，以及老师对这些音频做的标注，统称「录音数据」。",
      "联系邮箱：见课程通知。",
    ],
  },
  {
    number: "二",
    title: "授权内容",
    paragraphs: [
      `你同意将本项目中你的录音及其派生数据（包括切分片段、声学特征、标注结果等）的使用权授予 ${ORG_NAME} 及额外受益人 ${BENEFICIARY_NAME}老师（以下合称「${GRANTEES_LABEL}」）。`,
      `本授权为非独占、可转授给${GRANTEES_LABEL}合作的课程团队、在中国境内及境外均可使用，用于下面第三至第五条列明的三种用途。`,
      "你仍然是自己声音的主体；本授权不转让你的人格权益，也不妨碍你自己使用这些录音。",
    ],
  },
  {
    number: "三",
    title: "技术研究与模型训练",
    key: "train",
    paragraphs: [
      `你录的音频，以及老师对这些音频做的发音标注，会由${GRANTEES_LABEL}及其合作的课程团队用来训练和改进语音识别、发音评测这一类技术。`,
      "参与训练时，你的录音以编号出现，不带你的姓名、学号和联系方式。",
      `训练用的数据保存在${ORG_SHORT}的服务器上，只有项目组成员能够访问；不会公开发布原始录音。`,
      "如果某一条录音后来被判定质量不合格，它会被排除在训练之外。",
    ],
  },
  {
    number: "四",
    title: "教学展示与成果汇报",
    key: "display",
    paragraphs: [
      `在课堂教学、学术论文、项目结题汇报这类场合，${GRANTEES_LABEL}及其合作的课程团队可能播放你的录音片段，或者以波形图、拼音标注的形式展示。`,
      "展示时使用编号，不会出现你的姓名；如果场合确实需要署名，我们会另外单独征求你的同意。",
      "展示的目的是说明发音现象和教学方法，不会用来评价或者比较个人。",
    ],
  },
  {
    number: "五",
    title: "商业化产品",
    key: "commercial",
    paragraphs: [
      `基于这些语料训练出的模型，${GRANTEES_LABEL}及其合作的课程团队可能将其用在面向公众的产品或服务中，其中一部分可能是收费的。`,
      "产品中使用的是训练得到的模型，不会直接播放你的原始录音，除非我们事先另行征得你的同意。",
      "本协议不包含把你的声音用于语音合成、克隆你的音色，或者用于广告代言。",
    ],
  },
  {
    number: "六",
    title: "匿名化与数据安全",
    paragraphs: [
      "你的姓名、学号、联系方式与录音分开存放；对外使用的一律是编号。",
      `录音文件和标注结果存放在${ORG_SHORT}的服务器上，由${GRANTEES_LABEL}按角色授权访问：老师看得到你的进度和录音，标注专家只看得到音频和素材，管理员看不到标注内容。`,
      "导出的数据集只包含编号、音频和标注，不包含任何能直接认出你的信息。",
    ],
  },
  {
    number: "七",
    title: "撤回与变更",
    paragraphs: [
      `你可以向${ORG_SHORT}提出书面申请撤回授权；撤回自我们收到申请之日起生效，只影响之后的采集与新的数据集版本，不影响此前已经进行的处理、已经发布的数据集版本以及已经训练完成的模型。`,
      "撤回不需要说明理由，也不会影响你的课程成绩。",
      `撤回生效后，${GRANTEES_LABEL}及其合作的课程团队不会再采集你的新录音，也不会把你的录音放进之后的数据集版本。`,
      "如果本协议的内容有实质修改，我们会请你重新签署一次。",
      `撤回与数据相关的问题，由${ORG_NAME}负责处理。联系邮箱：见课程通知。`,
    ],
  },
];

/** The checkbox next to the single signature. */
export const CONSENT_AGREE_LABEL = `我已阅读并同意《${CONSENT_TITLE}》`;

export const GUARDIAN_TEXT =
  "你未满 18 周岁，还需要监护人一起确认。请在监护人在场时勾选下面这一项，并填写监护人姓名。";

// --------------------------------------------------------------------------- //
// compatibility — 我的 shows the three uses as a status list
// --------------------------------------------------------------------------- //

export interface ConsentItem {
  key: ConsentUseKey;
  title: string;
  summary: string;
  full: string[];
}

const SUMMARIES: Record<ConsentUseKey, string> = {
  train: "你的录音会用来训练和改进普通话发音评测的技术。",
  display: "你的录音片段可能在课堂、论文或项目汇报里被播放或引用。",
  commercial: "由本项目训练出的技术可能用在收费的产品或服务里。",
};

export const CONSENT_ITEMS: ConsentItem[] = CONSENT_CLAUSES.filter(
  (c): c is ConsentClause & { key: ConsentUseKey } => c.key !== undefined,
).map((c) => ({
  key: c.key,
  title: c.title,
  summary: SUMMARIES[c.key],
  full: c.paragraphs,
}));

/** Kept for 我的 / 撤回授权 copy. */
export const CONSENT_WITHDRAW_TEXT: string[] =
  CONSENT_CLAUSES.find((c) => c.title === "撤回与变更")?.paragraphs ?? [];

export const CONSENT_INTRO = CONSENT_SUMMARY[0]!;
